import { useEffect, useMemo, useRef } from "react";
import { translate } from "../config/translator";
import {LanguageLocaleEnum} from "../client/api";

type SelectorVisualsParams = {
  placeholder?: string;
  withoutChoose?: boolean;
  language: LanguageLocaleEnum;
  transparent?: boolean;
  value?: number | string;
  primary?: boolean;
  disabled?: boolean;
  iconL?: unknown;
  className?: string;
};

export const useSelectorVisuals = ({
  placeholder,
  withoutChoose,
  language,
  transparent,
  value,
  primary,
  disabled,
  iconL,
  className,
}: SelectorVisualsParams) => {
  const realPlaceholder = useMemo(() => {
    if (!placeholder) return withoutChoose ? "" : translate("select", language);
    return placeholder;
  }, [language, placeholder, withoutChoose]);

  const inputClass = useMemo(() => {
    if (transparent) {
      let style = "outline-0 flex items-center min-w-0 truncate";
      if (value !== undefined && value !== null) {
        if (primary) style += " !text-primary";
        else style += " text-font dark:text-font-dark";
      } else {
        style += " text-placeholder dark:text-placeholder-dark";
      }
      if (className) style += ` ${className}`;
      return style;
    }

    let style = "w-full outline-0 rounded-xl min-w-0 truncate";
    style +=
      " active:bg-input-bg-active active:dark:bg-input-bg-active-dark focus:bg-input-bg-active focus:dark:bg-input-bg-active-dark border active:dark:border-input-border-active-dark focus:dark:border-input-border-active-dark py-2";
    if (disabled) {
      style += " border-input-border opacity-60";
    } else {
      style +=
        " bg-input-bg dark:bg-input-bg-dark border-input-border dark:border-input-border-dark active:border-input-border-active focus:border-input-border-active";
    }
    if (value !== undefined && value !== null) {
      if (primary) style += " !text-primary";
      else style += " text-font dark:text-font-dark";
    } else {
      style += " text-placeholder dark:text-placeholder-dark";
    }
    if (iconL) style += " px-8";
    else style += " pl-2 pr-8";
    if (className) style += ` ${className}`;
    return style;
  }, [transparent, disabled, value, primary, iconL, className]);

  return { realPlaceholder, inputClass };
};

export const useSelectedLabel = <T extends { key: number | string; label: string }>(
  items: ReadonlyArray<T>,
  value?: number | string
) =>
  useMemo(() => items.find((item) => item.key === value)?.label, [items, value]);

export const useStableOnChange = <T extends (...args: any[]) => void>(handler: T) => {
  const ref = useRef(handler);
  useEffect(() => {
    ref.current = handler;
  }, [handler]);
  return ref;
};

